import React, { useEffect, useState } from 'react';
import platform from 'platform';
import { useLogger } from 'logging/writers/hooks';
import { useSelectAppMode } from 'app/store/general/selectors';
import { AppMode } from 'app/store/general/AppMode';
import './UnsupportedBrowserBanner.scss';
import { IconTooltip } from 'utils/components/IconTooltip';

function isSupportedChrome(name: string, version: number) {
    return name === 'chrome' && version >= 70;
}
function isSupportedFirefox(name: string, version: number) {
    return name === 'firefox' && version >= 60;
}
function isSupportedEdge(name: string, version: number) {
    return name === 'edge' && version >= 44;
}
function isSupportedSafari(name: string, version: number) {
    return name === 'safari' && version >= 12;
}
function isSupportedElectron(name: string, version: number) {
    return name === 'electron' && version >= 19;
}

let name: string | undefined;
let version: number | undefined;
let browserSupported = false;
let useMocks = false;

try {
    name = platform.name ? platform.name.toLowerCase() : undefined;
    version = platform.version
        ? parseInt(platform.version.split('.')[0], 10)
        : undefined;
} catch (error) {
    name = undefined;
    version = undefined;
}

browserSupported =
    name !== undefined &&
    name.length > 0 &&
    version !== undefined &&
    !isNaN(version) &&
    (isSupportedChrome(name, version) ||
        isSupportedFirefox(name, version) ||
        isSupportedEdge(name, version) ||
        isSupportedSafari(name, version) ||
        isSupportedElectron(name, version));

try {
    useMocks = window.location.search.toLowerCase().indexOf('usemocks') > -1;
} catch (error) {
    useMocks = false;
}

export interface UnsupportedBrowserBannerProps {
    onRendered: (rendered: boolean) => void;
}

const DismissedLocalStorageKey = 'UnsupportedBrowserBanner-dismissed';

export default function UnsupportedBrowserBanner(
    props: UnsupportedBrowserBannerProps
) {
    const logger = useLogger('UnsupportedBrowserBanner');
    const appMode = useSelectAppMode();
    const { onRendered } = props;

    const [dismissed, setDismissed] = useState(
        window.localStorage.getItem(DismissedLocalStorageKey)
    );

    useEffect(() => {
        if (!browserSupported) {
            logger.warn('Platform is not supported', platform);
        }
    }, [logger]);

    const shouldNotRender =
        useMocks ||
        browserSupported ||
        dismissed ||
        appMode !== AppMode.STANDALONE;

    useEffect(() => {
        onRendered(!shouldNotRender);
    }, [onRendered, shouldNotRender]);

    if (shouldNotRender) {
        return <></>;
    }

    const onDismiss = () => {
        window.localStorage.setItem(DismissedLocalStorageKey, 'true');
        setDismissed('true');
    };

    return (
        <div className="UnsupportedBrowserBanner">
            You are using an unsupported browser. Indigo is best experienced
            with a modern browser.{' '}
            <div className="info-icon">
                <IconTooltip>
                    <div>
                        For the best experience, use the latest version of
                        Google Chrome Desktop
                    </div>
                </IconTooltip>
            </div>
            <span className="dismiss" onClick={onDismiss}>
                Dismiss
            </span>
        </div>
    );
}
